import React from 'react';

/**
 * As in the Canvas App we are serving user project as SPA it doesn't trigger all the same document lifecycle events as would be triggered
 * in the published projects. This hook is used to simulate the DOMContentLoaded event that is triggered when the document has been completely loaded
 * and on each page switch.
 */
export const useSimulateDocumentLifecycleEvents = (
  pageId: string | undefined
) => {
  // If users custom code is mutating `head` and is adding new script tags programmatically on `DOMContentLoaded` event
  // user can end up in the endless cycle where even after initial load cycle `head` is modified, so isLoading becoming true
  // causing `DOMContentLoaded` will be re-triggered for the page and scripts started being added again. To avoid this we are tracking
  // if we already triggered events for the page. We don't need to keep specific page IDs as on page switch whole app is reloaded.
  const eventsTriggered = React.useRef<boolean>(false);
  const readyStateDescriptor = React.useRef<PropertyDescriptor | null>(null);

  React.useEffect(() => {
    // Store the original readyState descriptor
    readyStateDescriptor.current =
      Object.getOwnPropertyDescriptor(Document.prototype, 'readyState') || null;

    // Override readyState
    Object.defineProperty(document, 'readyState', {
      get: () => (eventsTriggered.current ? 'complete' : 'loading'),
      configurable: true,
    });

    if (pageId !== undefined && !eventsTriggered.current) {
      // Create the DOMContentLoaded event
      const event = new Event('DOMContentLoaded', {
        bubbles: true,
      });

      // Dispatch the event to simulate DOMContentLoaded
      document.dispatchEvent(event);

      // Create and dispatch event to simulate window load event
      const loadEvent = new Event('load');
      window.dispatchEvent(loadEvent);

      // Mark that events have been triggered
      eventsTriggered.current = true;

      // Indicate to site modules that the page is ready
      window?.Webflow?.ready();
    }

    // Cleanup: restore original readyState behavior
    return () => {
      if (readyStateDescriptor.current) {
        Object.defineProperty(
          document,
          'readyState',
          readyStateDescriptor.current
        );
      }
    };
  }, [pageId]);
};
